import styled, { css } from 'styled-components';
import { exFont } from '../../shared/typography';

// Questions step (tplA 504–583) and the form primitives the Marking step
// shares. Colours come only from ExamsScope's tokens.

export const StepPage = styled.div`
  display: flex;
  justify-content: center;
  padding: 28px 28px 40px;
  &[data-testid='questions-step'], &[data-layout='questions'] {
    padding: 28px 24px 40px;
  }
  &[data-testid='marking-step'], &[data-layout='marking'] {
    padding: 24px 24px 36px;
  }
`;

export const StepColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  max-width: 860px;
`;

export const QuestionsColumn = styled(StepColumn)`
  max-width: 832px;
  gap: 16px;
  & > :first-child {
    gap: 4px;
    h2,
    p {
      line-height: normal;
    }
  }
  label {
    font-size: 13px;
    line-height: normal;
  }
`;

export const StepHeading = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const H2 = styled.h2`
  ${exFont(700, 'xxl')}
  margin: 0;
  color: var(--ex-ink);
`;

export const Sub = styled.p`
  ${exFont(400, 'm')}
  margin: 0;
  color: var(--ex-ink-2);
`;

export const Card = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px 22px;
  background: var(--ex-surface);
  border: 1px solid var(--ex-line);
  border-radius: var(--ex-radius-card);
  box-shadow: var(--ex-shadow-1);
`;

export const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
  flex: ${({ $grow }) => ($grow ? '1 1 0' : '0 0 auto')};
`;

// Validation lines supply the gaps between sections on this step.
export const QuestionsCard = styled(Card)`
  gap: 0;
`;

export const SubjectField = styled(Field)`
  flex: 0 1 280px;
  max-width: 100%;
`;

export const Row = styled.div`
  display: flex;
  align-items: ${({ $align }) => $align || 'flex-end'};
  gap: 12px;
  flex-wrap: wrap;
`;

export const DetailsTitle = styled(Field)`
  margin: 16px 0 9px;
  label {
    font-size: 14px;
  }
  & > input {
    min-height: 44px;
  }
`;

export const DetailsRow = styled(Row)`
  align-items: flex-start;
  select {
    min-height: 44px;
  }
  ${Row} {
    min-height: 44px;
  }
  > :last-child {
    /* Match the fields' 6px gap and 16px validation line when State wraps. */
    padding-bottom: 22px;
  }
`;

export const Label = styled.label`
  ${exFont(600, 's')}
  color: var(--ex-ink-strong);
`;

export const Required = styled.span`
  color: var(--ex-bad);
  margin-left: 2px;
`;

export const Hint = styled.p`
  ${exFont(400, 'xs')}
  margin: 0;
  color: var(--ex-ink-muted);
`;

const control = css`
  ${exFont(400, 'm')}
  box-sizing: border-box;
  padding: 9px 12px;
  border: 1px solid
    ${({ $invalid }) => ($invalid ? 'var(--ex-bad)' : 'var(--ex-line-strong)')};
  border-radius: var(--ex-radius-item);
  background: var(--ex-surface);
  color: var(--ex-ink);
  &:focus {
    outline: none;
    border-color: var(--ex-purple);
  }
  &:disabled {
    background-color: var(--ex-surface-2);
    color: var(--ex-ink-muted);
    cursor: not-allowed;
  }
`;

export const Input = styled.input`
  ${control}
  width: 100%;
`;

export const Select = styled.select.attrs({ className: 'inset-select-arrow' })`
  ${control}
  min-width: 160px;
`;

export const TextArea = styled.textarea`
  ${control}
  width: 100%;
  min-height: 96px;
  resize: vertical;
`;

export const NumberInput = styled.input`
  ${control}
  width: 72px;
  text-align: center;
`;

export const LinkButton = styled.button`
  ${exFont(600, 's')}
  padding: 0;
  border: none;
  background: none;
  color: var(--ex-purple);
  cursor: pointer;
  &:disabled {
    color: var(--ex-ink-faint);
    cursor: not-allowed;
  }
  &:hover:not(:disabled) {
    text-decoration: underline;
  }
`;

export const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  padding: 0;
  border: none;
  border-radius: var(--ex-radius-item);
  background: transparent;
  color: var(--ex-ink-muted);
  cursor: pointer;
  &:hover:not(:disabled) {
    background: var(--ex-surface-2);
    color: var(--ex-ink);
  }
  &:disabled {
    color: var(--ex-ink-faint);
    cursor: not-allowed;
  }
`;

export const Chip = styled.span`
  ${exFont(500, 'xs')}
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 240px;
  padding: 4px 10px;
  border-radius: var(--ex-radius-pill);
  background: var(--ex-purple-100);
  color: var(--ex-purple);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const ChipOpen = styled.button`
  ${exFont(500, 'xs')}
  min-width: 0;
  padding: 0;
  border: none;
  background: none;
  color: inherit;
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:hover,
  &:focus-visible {
    text-decoration: underline;
  }
`;

export const ChipThumb = styled.img`
  width: 18px;
  height: 18px;
  border-radius: 4px;
  object-fit: cover;
  flex-shrink: 0;
`;

export const ChipRemove = styled.button`
  display: inline-flex;
  padding: 0;
  border: none;
  background: none;
  color: inherit;
  cursor: pointer;
`;

export const Note = styled.div`
  ${exFont(400, 's')}
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  border-radius: var(--ex-radius-item);
  ${({ $tone }) =>
    $tone === 'warn'
      ? css`
          background: var(--ex-warn-bg);
          color: var(--ex-warn-ink);
        `
      : $tone === 'ok'
      ? css`
          background: var(--ex-ok-bg);
          color: var(--ex-ok-ink);
        `
      : $tone === 'bad'
      ? css`
          background: var(--ex-bad-bg);
          color: var(--ex-bad-ink);
        `
      : css`
          background: var(--ex-purple-100);
          color: var(--ex-ink);
        `}
`;

export const NoteText = styled.p`
  margin: 0;
  flex: 1;
`;

// -------------------------------------------------------------- paper banner
export const PaperDrop = styled.div`
  ${exFont(400, 's')}
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  padding: 14px 16px;
  border: 1px dashed
    ${({ $over }) => ($over ? 'var(--ex-purple)' : 'var(--ex-purple-200)')};
  border-radius: var(--ex-radius-card);
  background: ${({ $over }) =>
    $over ? 'var(--ex-purple-100)' : 'var(--ex-surface-tint)'};
  color: var(--ex-ink-2);
`;

export const OptionalTag = styled.span`
  ${exFont(700, 'xs')}
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 2px 10px;
  border: 1px solid var(--ex-line-strong);
  border-radius: var(--ex-radius-pill);
  background: var(--ex-surface);
  color: var(--ex-ink-2);
`;

export const BannerText = styled.p`
  margin: 0;
  flex: 1;
  min-width: 200px;
  color: var(--ex-ink);
`;

export const Muted = styled.span`
  ${exFont(400, 'xs')}
  color: var(--ex-ink-muted);
`;

export const SmallSpinner = styled.span`
  width: 14px;
  height: 14px;
  border: 2px solid var(--ex-purple-200);
  border-top-color: var(--ex-purple);
  border-radius: 50%;
  animation: ex-spin 0.9s linear infinite;
  @keyframes ex-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

// ------------------------------------------------------------ question cards
export const QuestionsHead = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  & > span:first-child {
    font-size: 14px;
    line-height: normal;
  }
`;

export const TotalText = styled.span`
  ${exFont(600, 's')}
  margin-left: auto;
  color: var(--ex-ink-strong);
`;

export const CardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 10px;
  padding: 14px;
  border: 1px dashed var(--ex-purple-200);
  border-radius: var(--ex-radius-card);
  background: var(--ex-surface-tint);
  &:empty {
    display: none;
  }
`;

export const QCard = styled.div`
  border: 1px solid
    ${({ $invalid }) => ($invalid ? 'var(--ex-bad)' : 'var(--ex-line)')};
  border-radius: var(--ex-radius-card);
  background: var(--ex-surface);
  ${({ $dragging }) =>
    $dragging &&
    css`
      box-shadow: var(--ex-shadow-2);
      opacity: 0.9;
    `}
`;

export const QHead = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  cursor: pointer;
  border-bottom: 1px solid
    ${({ $open }) => ($open ? 'var(--ex-line)' : 'transparent')};
  button {
    width: 26px;
    height: 26px;
  }
  button[title='Duplicate'] {
    color: var(--ex-purple);
  }
  button[title='Delete'] {
    color: var(--ex-bad);
  }
  button[title='Collapse'],
  button[title='Expand'] {
    border-radius: 50%;
    background: var(--ex-surface-2);
  }
`;

export const DragHandle = styled.span`
  display: inline-flex;
  color: var(--ex-ink-faint);
  cursor: grab;
  touch-action: none;
`;

export const QTitle = styled.span`
  ${exFont(600, 'm')}
  color: var(--ex-ink);
  white-space: nowrap;
`;

export const MultiTag = styled.span`
  ${exFont(600, 'xs')}
  padding: 1px 6px;
  border-radius: 6px;
  background: var(--ex-purple-100);
  color: var(--ex-purple);
`;

export const Preview = styled.span`
  ${exFont(400, 's')}
  flex: 1;
  min-width: 0;
  color: var(--ex-ink-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const Spacer = styled.span`
  flex: 1;
`;

export const MarksTag = styled.span`
  ${exFont(500, 's')}
  color: var(--ex-ink-2);
  white-space: nowrap;
`;

export const QBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
`;

export const FieldHead = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const FieldTitle = styled.p`
  ${exFont(600, 's')}
  [data-testid='questions-step'] & {
    font-size: 14px;
    line-height: normal;
  }
  margin: 0;
  color: var(--ex-ink-strong);
`;

export const EditorFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

export const QuestionEditorFooter = styled(EditorFooter)`
  margin-top: -6px;
  padding: 10px 12px;
  border: 1px solid var(--ex-line-strong);
  border-top: 1px solid var(--ex-line);
  border-radius: 0 0 10px 10px;
  background: var(--ex-surface);
  & > div > button:last-child {
    min-height: 32px;
    padding: 0 14px;
    border: 1.5px solid var(--ex-purple);
    border-radius: 9px;
    background: var(--ex-surface);
  }
`;

export const PartsBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 14px;
  border-radius: var(--ex-radius-item);
  background: #f3f0f8;
  > p:first-child {
    ${exFont(600, 14)}
    color: var(--ex-purple);
  }
  button[data-part-add] {
    height: 42px;
    padding: 0 18px;
    border-radius: 10px;
    box-shadow: none;
  }
`;

export const AddRow = styled.div`
  margin-top: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  flex-wrap: wrap;
  > button {
    ${exFont(600, 14)}
    height: 42px;
    padding: 0 22px;
    background: var(--ex-surface);
    border: 1.5px solid var(--ex-purple);
    color: var(--ex-purple);
  }
`;

export const Or = styled.span`
  ${exFont(400, 's')}
  color: var(--ex-ink-muted);
`;

// --------------------------------------------------------------- path cards
export const PathOptions = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
`;

export const PathCard = styled.button`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  text-align: left;
  border: 1.5px solid
    ${({ $active, $invalid }) =>
      $active
        ? 'var(--ex-purple)'
        : $invalid
        ? 'var(--ex-bad)'
        : 'var(--ex-line-strong)'};
  border-radius: var(--ex-radius-card);
  background: ${({ $active }) =>
    $active ? 'var(--ex-selected-tint)' : 'var(--ex-surface)'};
  cursor: pointer;
  &:disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

export const Radio = styled.span`
  flex: 0 0 auto;
  width: 18px;
  height: 18px;
  box-sizing: border-box;
  margin-top: 1px;
  border-radius: 50%;
  border: 1.5px solid
    ${({ $active }) => ($active ? 'var(--ex-purple)' : 'var(--ex-line-strong)')};
  box-shadow: ${({ $active }) =>
    $active ? 'inset 0 0 0 3px var(--ex-surface)' : 'none'};
  background: ${({ $active }) =>
    $active ? 'var(--ex-purple)' : 'var(--ex-surface)'};
`;

export const PathTitle = styled.p`
  ${exFont(600, 15)}
  margin: 0;
  color: var(--ex-ink);
`;

export const PathNote = styled.p`
  ${exFont(400, 13.5)}
  line-height: 1.45;
  margin: 3px 0 0;
  color: #4a4458;
`;

// ------------------------------------------------------------------ toasts
export const UndoToastRoot = styled.div`
  ${exFont(500, 's')}
  display: flex;
  align-items: center;
  gap: 12px;
  color: var(--ex-ink);
`;

// ------------------------------------------------------------ stimulus form
export const StimulusPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px;
  border: 1px solid var(--ex-line);
  border-radius: var(--ex-radius-item);
  background: var(--ex-surface-tint);
`;

export const HiddenInput = styled.input`
  display: none;
`;

// Align question labels beside their controls, as in the exam design.
export const QuestionField = styled(Field)`
  display: grid;
  grid-template-columns: minmax(180px, 220px) minmax(0, 1fr);
  column-gap: 16px;
  & > :first-child {
    grid-column: 1;
    grid-row: 1 / span 4;
  }
  & > :not(:first-child) {
    grid-column: 2;
  }
`;

export const TaskRow = styled.div`
  display: grid;
  grid-template-columns: minmax(180px, 220px) minmax(0, 1fr);
  align-items: center;
  gap: 16px;
  & > select {
    width: 100%;
  }
`;
